import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useT } from '@/hooks/useT';
import { TopBar } from './TopBar';
// Wave I · SideNavHierarchical replaces the mode-rail SideNav.
// SideNav is kept imported so it doesn't break any other import site;
// it is no longer rendered by AppShell.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { SideNav as _SideNavLegacy } from './SideNav';
import { SideNavHierarchical } from './SideNavHierarchical';
import { MobileProjectHeader } from './MobileProjectHeader';
import { MobileBottomNav } from './MobileBottomNav';
import { OfflineBanner } from '@/components/ui/OfflineBanner';
import { UploadQueue } from '@/components/UploadQueue';
import { InstallPrompt } from '@/components/InstallPrompt';
// Wave 43 · Bug 5 — HouseAgent re-enabled.
// The old FAB (bottom-right anchor) was removed in XXXXX-fix because it
// overlapped TopBar avatars. It's now triggered via:
//   1. TopBar icon button (house icon, right cluster) dispatches housy:toggle.
//   2. Ctrl+H / Cmd+H keyboard shortcut (handled inside HouseAgent).
// The component mounts at the app root but renders NO visible FAB
// (the floating house bubble is now hidden; only the panel is visible when
// open). The TopBar button is the single entry point.
import { HouseAgent } from '@/components/house/HouseAgent';
import { FeatureBoundary } from '@/components/ErrorBoundary';
import { OnboardingProvider } from '@/onboarding/OnboardingContext';
import { TourOrchestrator } from '@/onboarding/TourOrchestrator';
// Wave help-tour-mvp · 2026-05-20 — interactive 8-step MVP tour.
// Coexists with the server-driven TourOrchestrator (legacy); this one
// is client-only, opt-in via openTour() from anywhere in the tree.
import { Tour } from '@/components/onboarding/Tour';
import { useTourEvents } from '@/components/onboarding/useTourTrigger';
import { useAuth } from '@/store/auth.store';
import { cn } from '@/lib/cn';
import { CommandBar } from '@/components/command-bar/CommandBar';
// W123.4 · 2026-05-26 · Ajuda contextual (substitui Housy como módulo PM).
import {
  AjudaContextualProvider,
  AjudaContextualMount,
} from '@/components/ajuda/AjudaContextual';
import { ChatPanel } from '@/components/chat-instant/ChatPanel';
import { ChatWindowsRoot } from '@/components/chat-instant/ChatWindowsRoot';
import { ChatNotificationDock } from '@/components/chat-instant/ChatNotificationDock';
import { ChatMinimizedPills } from '@/components/chat-instant/ChatMinimizedPills';
import { KeyboardShortcutsHelp } from '@/components/keyboard/KeyboardShortcutsHelp';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';

/**
 * Wave 341 · contentWidth lever.
 *
 * Default `narrow` (max-w-3xl, 768 px) is right for forms and prose
 * but cramped for tables/dashboards on a 1440-px monitor. Pages opt in
 * to wider tracks per their content type:
 *
 *   - narrow   · max-w-3xl  (768 px) — default, forms, single-column reading
 *   - standard · max-w-5xl  (1024 px) — most pages with mixed content
 *   - wide     · max-w-7xl  (1280 px) — data-heavy: BI, finance, tables
 *   - full     · w-full              — admin/system views that *fill* the screen
 *
 * Mobile is unaffected — every track uses w-full + the same px-4 gutters,
 * so the cap only matters once the viewport exceeds the cap. No new
 * mobile horizontal scroll, no breakpoint-specific overflow risk.
 */
export type ContentWidth = 'narrow' | 'standard' | 'wide' | 'full';

interface AppShellProps {
  children: ReactNode;
  contentWidth?: ContentWidth;
  /**
   * W132.45 — Minimal shell for foreman/worker surfaces (CampoPage).
   * When true, suppresses the full SideNav, TopBar, BottomNav and heavy
   * global widgets. Only renders OfflineBanner + children.
   */
  minimal?: boolean;
}

const CONTENT_WIDTH_CLASS: Record<ContentWidth, string> = {
  narrow: 'max-w-3xl',
  standard: 'max-w-5xl',
  wide: 'max-w-7xl',
  full: 'max-w-none',
};

const COLLAPSE_KEY = 'builder.sidenav.collapsed';

/**
 * Wave 100 v3 — Pro sidebar shell.
 *
 * Replaces the old avatar-dropdown nav with a persistent left rail
 * (collapsible to icon-only) plus a slimmed TopBar that keeps utility
 * controls (Cmd-K palette, theme, presence, inbox bell, account
 * mini-menu).
 *
 * State held here:
 *   - `sidebarOpen`     — mobile drawer visibility
 *   - `sidebarCollapsed` — desktop rail mode (icon-only vs full-width)
 *
 * The collapse choice is persisted to localStorage so it survives a
 * reload, matching the muscle memory of "I closed it once, leave it
 * closed". Mobile drawer always starts closed.
 *
 * Layout: SideNav is `position: fixed left-0` so the rest of the
 * page just gets a left padding equal to the rail width. We animate
 * the padding so the content reflows smoothly when the rail shrinks.
 *
 * Owner/buyer portals don't render AppShell — they use PortalShell
 * — so no role-gating needed here, the SideNav handles its own
 * visibility based on user role.
 */
// Wave 379AM · default flipped 'narrow' → 'full'. JP: "isso deveria ser
// padrão pra 100% das telas". Antes o default era max-w-3xl (768px), o
// que jogava ~⅔ da viewport como margem preta vazia em desktop. Páginas
// que precisam de coluna estreita (login, forms simples) explicitam.
export function AppShell({ children, contentWidth = 'full', minimal = false }: AppShellProps) {
  const user = useAuth((s) => s.user);
  const location = useLocation();
  const { locale } = useT();
  const isEn = locale === 'en';
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Wave help-tour-mvp · 2026-05-20 — Tour overlay visibility. Opened
  // via openTour() event from WelcomePage/TopBar menu/post-create hook.
  const [tourOpen, setTourOpen] = useState(false);
  useTourEvents(
    () => setTourOpen(true),
    () => setTourOpen(false),
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    // VVVVV-followup: happy-dom / test envs may have window but throw on
    // localStorage access (privacy mode, sandbox, blocked iframe). Wrap defensively.
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage?.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [cmdBarOpen, setCmdBarOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage?.setItem(COLLAPSE_KEY, sidebarCollapsed ? '1' : '0');
    } catch {
      // localStorage unavailable — non-fatal; collapse state is per-tab only.
    }
  }, [sidebarCollapsed]);

  // Global Ctrl+K / Cmd+K opens the cross-module command bar.
  // "/" while not focused in an input also opens it.
  useEffect(() => {
    if (!user) return;
    const onKey = (e: KeyboardEvent) => {
      // Ctrl+K / Cmd+K — always open regardless of focus.
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setCmdBarOpen((v) => !v);
        return;
      }
      // "/" while not in a text field.
      if (e.key === '/') {
        const tag = (e.target as HTMLElement)?.tagName?.toLowerCase() ?? '';
        const editable = (e.target as HTMLElement)?.isContentEditable;
        if (tag === 'input' || tag === 'textarea' || tag === 'select' || editable) return;
        e.preventDefault();
        setCmdBarOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [user]);

  // DP5 · Global keyboard shortcuts (g+h, g+p, g+i, g+n, ?)
  const { shortcutsHelpOpen, setShortcutsHelpOpen } = useKeyboardShortcuts({
    // AppShell already owns Cmd+K → cmdBarOpen; don't double-handle.
    onOpenCmdBar: undefined,
  });

  // W132.45 — minimal shell for foreman/worker surfaces.
  if (minimal) {
    return (
      <div className="relative min-h-full" data-testid="app-shell">
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-brand-500 focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-brand-500"
        >
          {isEn ? 'Skip to content' : 'Pular para o conteúdo'}
        </a>
        <OfflineBanner />
        <main id="main" className="flex-1 w-full min-w-0 overflow-x-hidden">
          {children}
        </main>
      </div>
    );
  }

  const hasSidebar = !!user;
  // Padding kicks in only at md+ where the sidebar is actually
  // anchored — on mobile the rail is off-canvas via translate so
  // padding would just leave a phantom gutter.
  const mainPadClass = !hasSidebar
    ? ''
    : sidebarCollapsed
      ? 'md:pl-[68px]'
      : 'md:pl-[252px]';

  // Wave Onda1 · Detect project sub-route for mobile contextual header.
  // Pattern: /projects/<id>/<module>[/...]
  const projectRouteMatch = location.pathname.match(/^\/projects\/([^/]+)(\/.*)?$/);
  const isProjectRoute = !!projectRouteMatch && !!user;
  const mobileProjectId = projectRouteMatch?.[1] ?? '';

  return (
    <OnboardingProvider>
      <AjudaContextualProvider>
      <div className="relative min-h-full" data-testid="app-shell">
        {/* Skip-link: first focusable element, visually hidden until focused.
            Lets keyboard users jump past the nav straight to the page body. */}
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-brand-500 focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-brand-500"
        >
          {isEn ? 'Skip to content' : 'Pular para o conteúdo'}
        </a>
        {hasSidebar && (
          <SideNavHierarchical
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            collapsed={sidebarCollapsed}
            onToggleCollapsed={() => setSidebarCollapsed((c) => !c)}
          />
        )}

        <div
          className={cn(
            'flex min-h-full flex-col transition-[padding] duration-200 ease-out',
            mainPadClass,
          )}
        >
          <OfflineBanner />
          {/* Wave cmdk-unified · onOpenSearch connects TopBar's search
              button to the single Cmd+K handler owned by AppShell. */}
          {/* Wave Onda1 · On mobile, project routes show a compact contextual
              header instead of the full TopBar. Desktop always shows TopBar. */}
          {isProjectRoute ? (
            <>
              <MobileProjectHeader
                projectId={mobileProjectId}
                onOpenAccount={() => setSidebarOpen(true)}
              />
              {/* TopBar still renders but is hidden on mobile via md:flex */}
              <div className="hidden md:block">
                <TopBar
                  onOpenSidebar={() => setSidebarOpen(true)}
                  sidebarMounted={hasSidebar}
                  onOpenSearch={() => setCmdBarOpen(true)}
                />
              </div>
            </>
          ) : (
            <TopBar
              onOpenSidebar={() => setSidebarOpen(true)}
              sidebarMounted={hasSidebar}
              onOpenSearch={() => setCmdBarOpen(true)}
            />
          )}
          {/* Wave 332c · main wrapper:
              - min-w-0 on the parent flex column lets nested truncate /
                tables shrink instead of overflowing the viewport.
              - overflow-x-hidden absorbs occasional 1-2px horizontal
                bleed (Recharts SVG, code blocks, long URLs in messages)
                so the page never gets a phantom side-scroll on mobile.
              - pb-28 on mobile keeps content above BottomTabs (60px) +
                Housy mark (24px clearance). Was pb-24 → JP saw the last
                row clipping. */}
          <main
            id="main"
            className={cn(
              // Wave 341 · width is no longer hard-coded. The cap class
              // comes from CONTENT_WIDTH_CLASS so each page can opt
              // into a wider track. Mobile is identical across modes.
              'flex-1 mx-auto w-full min-w-0 overflow-x-hidden px-4 pb-28 pt-4 sm:pb-24',
              CONTENT_WIDTH_CLASS[contentWidth],
            )}
          >
            {children}
          </main>
          {/* Wave I · hierarchical mobile bottom nav replaces BottomTabs
              now that SideNavHierarchical is the active sidebar. */}
          {hasSidebar && <MobileBottomNav />}
        </div>

        <UploadQueue />
        <InstallPrompt />
        {/* Wave 43 · Bug 5 — HouseAgent re-enabled as TopBar-triggered panel.
            The FAB bottom-right anchor is gone; the panel is opened via
            TopBar house icon button (dispatches housy:toggle) or Ctrl+H.
            FeatureBoundary swallows any render error so a Housy crash
            never takes down the whole shell. */}
        {/* W158 · 2026-06-02 — Housy (e o atalho Ctrl+H) é superfície de
            equipe. Cliente/corretor ficam no portal externo sem o assistente
            nem o atalho — antes o componente montava para todos via {user}. */}
        {user && user.role !== 'client' && user.role !== 'broker' && (
          <FeatureBoundary name="Housy"><HouseAgent /></FeatureBoundary>
        )}
        {/* Wave eeeee · Cross-module command bar (Ctrl+K / Cmd+K / "/"). */}
        {user && (
          <CommandBar open={cmdBarOpen} onClose={() => setCmdBarOpen(false)} />
        )}
        {/* Wave ttttt · 2026-05-16
            VoiceMicButton globally mounted FAB removed. The bola
            crashed every screen's hierarchy and wasn't useful outside
            Campo. Voice capture stays inline (InlineVoiceRecorder) on
            Campo / RDO entry. */}
        {/* Onboarding checklist + tooltip/beacon overlays.
            TourOrchestrator no longer mounts globally — it is rendered
            inside each page that opts into tour guidance so slideout
            steps never block unrelated areas of the app. The checklist
            widget (bottom-right pill) stays global via OnboardingChecklist,
            which TourOrchestrator renders even when no step is active. */}
        <TourOrchestrator />
        {/* Wave chat-instant · Drawer lateral (Conversas) + janelas
            floating. (O ChatBar/chat.store legado da Wave chat-mvp foi
            removido — chat real é chat-instant.) SEPARADO do InboxBell
            (Wave 45) e da página /inbox formal. */}
        {user && <ChatPanel />}
        {user && <ChatWindowsRoot />}
        {/* Bolhas flutuantes bottom-right pra new-msg notifications quando
            o panel está fechado. Anti-flood: aggregação por sender, cap
            3 visíveis, auto-dismiss 8s. Sem som. */}
        {user && <ChatNotificationDock />}
        {/* Wave chat-pills · 2026-05-21
            Pills persistentes Messenger-style. Aparecem ao enviar OU
            receber msg, somem após 30min sem atividade. SEPARADOS do
            ChatNotificationDock acima: dock é EPHEMERAL (8s preview),
            pills são PERSISTENT (30min TTL). Vivem no bottom-right do
            viewport e o dock se desloca pra cima quando pills aparecem. */}
        {user && <ChatMinimizedPills />}
        {/* DP5 · Keyboard shortcuts help drawer ("?" key). */}
        <KeyboardShortcutsHelp
          open={shortcutsHelpOpen}
          onClose={() => setShortcutsHelpOpen(false)}
        />
        {/* Wave help-tour-mvp · 2026-05-20 — interactive 8-step tour overlay.
            Hidden unless openTour() event was dispatched. Admin-only step
            (Comando) is filtered out when user is not admin. */}
        <Tour
          open={tourOpen}
          onClose={() => setTourOpen(false)}
          isAdmin={user?.role === 'admin'}
        />
        {/* W123.4 · 2026-05-26 · Ícone "?" + drawer 320px no canto sup. dir.
            Substitui marca "Housy" como módulo. Renderiza só quando a
            página atual publicou um contexto via useSetAjudaContext. */}
        {user && <AjudaContextualMount />}
      </div>
      </AjudaContextualProvider>
    </OnboardingProvider>
  );
}
